import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { LiaCarSideSolid } from 'react-icons/lia';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Actions } from '../../../components/Actions';
import { Loading } from '../../../components/Loading';
import { PageTitle } from '../../../components/PageTitle';
import StyledCard from '../../../components/StyledCard';
import { StyledInput } from '../../../components/StyledInput';
import { useUser } from '../../../context/UserContext';
import { consultDebtsToUpdateInfo } from '../../../hooks/useDebtsUserVehicle';
import { useDriverInfo } from '../../../hooks/useDriver';
import {
  IUserVehicle,
  getUserVehicleById,
  getVehicle,
  postVehicle,
  putVehicle,
} from '../../../hooks/useVeiculos';
import theme from '../../../styles/themes/default';
import {
  FUEL_TYPE,
  REGISTER_ORIGIN,
  UF,
  VEHICLE_TYPE,
} from '../../../utils/enums';
import formatCpf from '../../../utils/formatCpf';
import { formatCurrencyRegisterVehicleForm } from '../../../utils/formatCurrencyRegisterVehicleForm';
import { validateCpf } from '../../../utils/validateCpf';
import { StyledSelect } from '../components/StyledSelect';
import { Form, FormWrapper, InputLabel, VehicleContainer } from './style';

const VehicleForm: FC = () => {
  const { isLoggedIn, user } = useUser();

  let [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const id = searchParams.get('id');

  const [plate, setPlate] = useState('');
  const [uf, setUf] = useState('');
  const [owner, setOwner] = useState('');
  const [cpf, setCpf] = useState('');
  const [type, setType] = useState('');
  const [venalValue, setVenalValue] = useState('');
  const [renavam, setRenavam] = useState('');
  const [manufactureYear, setManufactureYear] = useState('');
  const [model, setModel] = useState('');
  const [modelYear, setModelYear] = useState('');
  const [brand, setBrand] = useState('');
  const [color, setColor] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [driver, setDriver] = useState('');
  const [driverOptions, setDriverOptions] = useState([]);

  const [vehicleId, setVehicleId] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [documentRequired, setDocumentRequired] = useState(false);

  const handleChangePlate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPlate(e.target.value.toUpperCase());
    },
    [setPlate],
  );

  const handleChangeOwner = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOwner(e.target.value);
    },
    [setOwner],
  );

  const handleChangeVenalValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let inputString = e.currentTarget.value.replace(/[^\d,]/g, '');

      const newInputString = formatCurrencyRegisterVehicleForm(inputString);

      setVenalValue(newInputString);
    },
    [setVenalValue],
  );

  const handleChangeCpf = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCpf(formatCpf(e.target.value));
    },
    [setCpf],
  );

  const handleChangeRenavam = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replaceAll(/\D/g, '');
      setRenavam(value);
    },
    [setRenavam],
  );

  const handleChangeManufactureYear = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replaceAll(/\D/g, '');
      setManufactureYear(value);
    },
    [setManufactureYear],
  );

  const handleChangeModel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setModel(e.target.value);
    },
    [setModel],
  );

  const handleChangeModelYear = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replaceAll(/\D/g, '');
      setModelYear(value);
    },
    [setModelYear],
  );

  const handleChangeBrand = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBrand(e.target.value);
    },
    [setBrand],
  );

  const handleChangeColor = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setColor(e.target.value);
    },
    [setColor],
  );

  const searchVehicle = useCallback(async () => {
    try {
      setIsLoading(true);
      const { vehicle, documentRequired } = await getVehicle({
        placa: plate,
        uf,
        email: user?.email ?? '',
        renavam: renavam,
      });
      if (vehicle.ano) {
        setManufactureYear(vehicle.ano);
      }
      if (vehicle.marca !== '') {
        setBrand(vehicle.marca);
      }
      if (vehicle.uf !== 'indisponível') {
        setUf(vehicle.uf);
      }
      if (vehicle.renavam) {
        const result = vehicle.renavam.toString();
        setRenavam(result);
      }
      if (vehicle.tipo) {
        setType(vehicle.tipo.toUpperCase());
      }
      setVehicleId(vehicle.id);

      if (documentRequired) {
        setDocumentRequired(documentRequired);
        Swal.fire({
          icon: 'info',
          title: 'Atenção!',
          html: `Favor informar o CPF do proprietário do veículo.`,
          confirmButtonColor: theme.colors.orange.primary,
        });
      }
    } catch (e: any) {
      let message = `Não foi possível encontrar o veículo. Verifique se a placa e UF estão corretos.`;

      if (e?.response?.data?.message === 'Error on find vehicle information') {
        message = 'Favor informar o Renavam do veículo.';
      }
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html: message,
        confirmButtonColor: theme.colors.orange.primary,
      });
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [plate, renavam, uf, user?.email]);

  const requiredField = (value: any) => {
    return value ? '' : 'Campo obrigatório';
  };

  const cpfError = useMemo(() => {
    if (!cpf && !documentRequired) {
      return '';
    }

    const validation = validateCpf({
      cpf,
      emptyCpf: 'Campo obrigatório',
      incorrectCpf: 'CPF inválido',
    });
    return validation.message;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpf, uf, documentRequired]);

  const isFormValid = useCallback(() => {
    const validations = [
      requiredField(plate),
      requiredField(uf),
      cpfError,
      requiredField(renavam),
    ];
    return !validations.some((v) => v);
  }, [plate, uf, cpfError, renavam]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!vehicleId) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html: `Não foi possível encontrar o veículo. Verifique se a placa e UF estão corretos.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      return;
    }
    setValidating(true);
    if (!isFormValid()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos inválidos!',
        html: `Alguns campos ainda estão inválidos. Corrija-os e tente novamente.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      return;
    }

    try {
      setIsLoading(true);
      const payload: IUserVehicle = {
        color,
        document: cpf,
        fuelType,
        idVehicle: vehicleId,
        owner,
        renavam,
        uf,
        venalValue: venalValue
          ? parseFloat(venalValue.replace('.', ''))
          : undefined,
        model,
        modelYear: parseInt(modelYear),
        driverId: driver ? Number(driver) : undefined,
        showNotification: false,
      };

      if (id) {
        await putVehicle(Number(id), payload);
      } else {
        payload.origin = REGISTER_ORIGIN.FROTAS;

        const created = await postVehicle(payload);
        if (created.id && created.uf !== 'SE') {
          try {
            await consultDebtsToUpdateInfo(created.id, true);
          } catch (error: any) {
            console.log(
              '🚀 ~ file: index.tsx:244 ~ handleSubmit ~ error:',
              error,
            );
            if (
              error?.response?.status === 404 &&
              (error?.response?.data?.message?.includes(
                'Nenhum débito encontrado',
              ) ||
                error?.response?.data?.message?.includes(
                  'Débitos não encontrados',
                ) ||
                error?.response?.data?.message?.includes(
                  'Erro consultando débitos',
                ))
            ) {
              Swal.fire({
                icon: 'info',
                title: 'Erro!',
                html: `Não foi encontrado débitos para este veículo. Mas o veículo foi cadastrado com sucesso.`,
                confirmButtonColor: theme.colors.orange.primary,
              }).then(() =>
                navigate('/vehicles', {
                  state: {
                    shouldRefetch: true,
                  },
                }),
              );
              return;
            } else if (
              error?.response?.status === 403 &&
              error?.response?.data?.message?.includes(
                'Limite de consultas atingido',
              )
            ) {
              Swal.fire({
                icon: 'info',
                title: 'Erro!',
                html: `Não foi possível consultar os débitos do veículo, pois o limite de consultas foi atingido. Mas o veículo foi cadastrado com sucesso.`,
                confirmButtonColor: theme.colors.orange.primary,
              }).then(() =>
                navigate('/vehicles', {
                  state: {
                    shouldRefetch: true,
                  },
                }),
              );
              return;
            } else if (
              error?.response?.status === 404 &&
              error?.response?.data?.message?.includes('DÍVIDA ATIVA')
            ) {
              Swal.fire({
                icon: 'info',
                title: 'Alerta!',
                html: `Este Veículo está com Dívida Ativa - Veja nos Detalhes. Seu veículo foi cadastrado com sucesso.`,
                confirmButtonColor: theme.colors.orange.primary,
              }).then(() =>
                navigate('/vehicles', {
                  state: {
                    shouldRefetch: true,
                  },
                }),
              );
              return;
            }

            return Swal.fire({
              icon: 'error',
              title: 'Erro!',
              html:
                error?.response?.data?.message ??
                `Não foi possível registrar o veículo.`,
              confirmButtonColor: theme.colors.orange.primary,
            });
          }
        }
      }
      let additionalMessage = ``;
      if (!id && uf === 'SE') {
        additionalMessage = `<span>Consultas de débitos do Sergipe devem ser realizadas através do 
          <a href="https://www.detran.se.gov.br/portal/?pg=cons_veiculo" target="_blank">Detran SE</a> 
          e parceladas através da Zignet.</span>`;
      }
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        html: `Veículo ${
          id ? 'editado' : 'cadastrado'
        } com sucesso. ${additionalMessage}`,
        confirmButtonColor: theme.colors.orange.primary,
      }).then(() =>
        navigate('/vehicles', {
          state: {
            shouldRefetch: true,
          },
        }),
      );
      return;
    } catch (e: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html:
          e?.response?.data?.message ?? `Não foi possível registrar o veículo.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadVehicle = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const vehicle = await getUserVehicleById(parseInt(id));

      setVehicleId(vehicle.idVehicle);
      setUf(vehicle.uf ?? vehicle?.vehicle?.uf);
      setColor(vehicle.color);
      setCpf(vehicle.document);
      setFuelType(vehicle.fuelType);
      setOwner(vehicle.owner);
      setRenavam(
        (vehicle.renavam
          ? vehicle.renavam
          : vehicle?.vehicle?.renavam?.toString()) ?? '',
      );
      vehicle.venalValue &&
        setVenalValue(
          formatCurrencyRegisterVehicleForm(
            parseFloat(vehicle.venalValue.toString()).toString(),
          ),
        );
      setBrand(vehicle.vehicle?.marca ?? '');
      setPlate(vehicle.vehicle?.placa ?? '');
      setModel(vehicle.model);
      setModelYear((vehicle.modelYear ?? '').toString());
      setManufactureYear(vehicle.vehicle?.ano ?? '');
      setType(vehicle.vehicle?.tipo ?? '');
      setDriver((vehicle?.driver?.id ?? '').toString());
    } catch (e: any) {
      console.log(e);
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html: e?.response?.data?.message ?? `Veículo não encontrado.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      navigate('/vehicles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormatOptions = () => {
    const result: any = [];

    driversResult?.results.map((driver) =>
      result.push({ id: driver.id, value: driver.name }),
    );

    setDriverOptions(result);
  };

  const { data: driversResult } = useDriverInfo({
    resultsPerPage: 999999999999,
    page: 1,
    orderByDirection: 'asc',
    orderBy: 'updated_at',
  });

  useEffect(() => {
    handleFormatOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driversResult]);

  useEffect(() => {
    if (isLoggedIn) {
      loadVehicle();
    }
  }, [id, isLoggedIn]);

  useEffect(() => {
    if (plate.length === 7 && uf && !id) {
      searchVehicle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, plate, uf]);

  return (
    <VehicleContainer>
      {isLoading && <Loading />}
      <PageTitle
        title={id ? 'Edição' : 'Novo Cadastro'}
        icon={<LiaCarSideSolid />}
      />
      <Form onSubmit={handleSubmit}>
        <StyledCard>
          <h2>Informações Gerais</h2>
          <FormWrapper>
            <InputLabel gridColumnStart={1} gridColumnEnd={3}>
              <StyledInput
                placeholder="Digite a placa para buscar"
                value={plate}
                maxLength={7}
                onChange={handleChangePlate}
                required
                error={validating && !!requiredField(plate)}
                errorMessage={requiredField(plate)}
                disabled={!!id}
              />
            </InputLabel>
            <StyledSelect
              placeholder="Selecione a UF"
              value={uf}
              setValue={setUf}
              options={Object.keys(UF).map((key) => ({
                id: key,
                value: UF[key as keyof typeof UF],
              }))}
              error={validating && !!requiredField(uf)}
              errorMessage={requiredField(uf)}
            />
            <StyledInput
              placeholder="Proprietário"
              value={owner}
              onChange={handleChangeOwner}
            />
            <StyledInput
              placeholder="CPF do proprietário"
              value={cpf}
              onChange={handleChangeCpf}
              maxLength={14}
              error={validating && !!cpfError}
              errorMessage={cpfError}
            />
            <StyledSelect
              placeholder="Tipo de Veículo"
              value={type}
              options={Object.keys(VEHICLE_TYPE).map((key) => ({
                id: key,
                value: VEHICLE_TYPE[key as keyof typeof VEHICLE_TYPE],
              }))}
              setValue={setType}
            />
            <StyledInput
              placeholder="Valor venal"
              value={venalValue}
              onChange={handleChangeVenalValue}
              type="text"
              maxLength={13}
            />
            <StyledInput
              placeholder="Renavam"
              value={renavam}
              onChange={handleChangeRenavam}
              maxLength={11}
              error={validating && !!requiredField(renavam)}
              errorMessage={requiredField(renavam)}
            />
            <StyledInput
              placeholder="Ano de fabricação"
              value={manufactureYear}
              onChange={handleChangeManufactureYear}
              maxLength={4}
            />
            <StyledInput
              placeholder="Digite o modelo"
              value={model}
              onChange={handleChangeModel}
            />
            <StyledInput
              placeholder="Ano do modelo"
              value={modelYear}
              onChange={handleChangeModelYear}
              maxLength={4}
            />
            <StyledInput
              placeholder="Digite a marca"
              value={brand}
              onChange={handleChangeBrand}
              disabled={!!id}
            />
            <StyledInput
              placeholder="Digite a cor"
              value={color}
              onChange={handleChangeColor}
            />
            <StyledSelect
              placeholder="Selecione o tipo de combustível"
              value={fuelType}
              options={Object.keys(FUEL_TYPE).map((key) => ({
                id: key,
                value: FUEL_TYPE[key as keyof typeof FUEL_TYPE],
              }))}
              setValue={setFuelType}
            />
            <StyledSelect
              placeholder="Selecione o condutor"
              value={driver}
              options={driverOptions}
              setValue={setDriver}
            />
          </FormWrapper>
        </StyledCard>
        <Actions onCancel={() => navigate('/vehicles')} />
      </Form>
    </VehicleContainer>
  );
};

export default VehicleForm;
